import type { Query } from "@tanstack/react-query";

/** Keep paginated list pages warm while the user moves Prev/Next in one session. */
export const PAGINATED_SEARCH_STALE_MS = 10 * 1000;
export const PAGINATED_SEARCH_GC_MS = 30 * 60 * 1000;

export const paginatedSearchQueryOptions = {
  staleTime: PAGINATED_SEARCH_STALE_MS,
  gcTime: PAGINATED_SEARCH_GC_MS,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

function sameFiltersExceptCursor(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  cursorField: string,
): boolean {
  const strip = (o: Record<string, unknown>) => {
    const next = { ...o };
    delete next[cursorField];
    return JSON.stringify(next);
  };
  return strip(a) === strip(b);
}

/** True while a filter search is in flight (not cursor pagination with placeholder rows). */
export function isFilterSearchLoading(
  isLoading: boolean,
  isFetching: boolean,
  isPlaceholderData: boolean,
): boolean {
  return isLoading || (isFetching && !isPlaceholderData);
}

/** Show the prior page while the next/prev cursor request is in flight (same filters only). */
export function keepPreviousPageData<TData>(
  currentPageKey: Record<string, unknown>,
  cursorField = "after",
) {
  return (
    previousData: TData | undefined,
    previousQuery: Query<TData, Error, TData, readonly unknown[]> | undefined,
  ): TData | undefined => {
    if (!previousData || !previousQuery) return undefined;
    const prevPageKey = previousQuery.queryKey.at(-1);
    if (typeof prevPageKey !== "object" || prevPageKey === null) return undefined;
    if (
      !sameFiltersExceptCursor(
        prevPageKey as Record<string, unknown>,
        currentPageKey,
        cursorField,
      )
    ) {
      return undefined;
    }
    return previousData;
  };
}
